// ─────────────────────────────────────────────────────────────────────────
// cscSizing.js — CSC-only shared sizing / battery "mechanism".
//
// Extracted verbatim from cscCombinedTab.jsx (the source of truth — it is
// LIVE production commercial-pricing code) to eliminate copy-paste drift
// with cscMeterRow.jsx (the Multi-Meter per-meter row), which previously
// carried an independent copy of the same functions.
//
// Every function here is a PURE function of its explicit inputs — no React
// state/hooks, no JSX. Values that used to be closed-over component state
// (bill, tariffRate, candidates, snap96, etc.) are now explicit parameters,
// usually bundled into a single `ctx` object for cleaner call sites.
//
// CSC/RSC boundary: this module may import pricing primitives that already
// exist in useRscPricing (commercialPricePerKwp, batteryPricePerKwh,
// parseInverterKw) but must NEVER import RSC's generateCandidates /
// calcSaving / proposalEngine. CSC keeps its own calculation — see
// feedback_csc_never_mix_rsc.md.
// ─────────────────────────────────────────────────────────────────────────

// Battery cost folded into the system total of generated docs. The PRODUCT depends on the
// battery size, not the system kWp (user 2026-07-10):
//   < 100 kWh  → LV battery, 16-kWh units @ RM 7,800 each (ceil(kWh/16) × 7,800) → Hybrid
//     (net = ATAP 8,600 − 800/battery promo; matches RSC + create-sales. user 2026-08-03)
//   ≥ 100 kWh  → HV commercial battery, per-kWh @ RM 1,200 (RM 1,100 above 1000 kWh) → String
// Steps: 16·32·48·64·80·96 (LV) then 100·200·261 (HV). Any system size may pick either.
// LV 16-kWh battery unit NET price = ATAP battery gross (Battery Pricing table) − the RSC
// per-battery promo. Data-driven twice over: the gross comes from the pricing DB via
// setLvBattUnitNet, and the promo is RSC's PER_BATTERY_PROMO rather than a literal — it
// moved from 800 to 1,200 on 2026-08-21 and CSC was still subtracting 800, pricing a 16kWh
// battery at 7,800 while RSC quoted 7,400 for the same unit.
// Fallback below is 8,600 − 1,200 = 7,400, used only until pricing loads.
let LV_BATT_UNIT_NET = 7400;
export const setLvBattUnitNet = (v) => { const n = Number(v) || 0; if (n > 0) LV_BATT_UNIT_NET = n; };
// Battery BRAND (user 2026-09-21). 'goodwe' is everything that existed before — LV 16-kWh
// units and the HV 261-kWh block — unchanged. 'huawei' is a single commercial HV product sold
// ONLY in 241-kWh blocks (241 · 482 · 723 …) at RM 1,350/kWh flat. Any card with no brand
// set is GoodWe, so saved drafts and live quotes keep their prices.
export const BATT_BRAND_GOODWE = 'goodwe';
export const BATT_BRAND_HUAWEI = 'huawei';
export const HUAWEI_BATT_BLOCK_KWH = 241;
export const HUAWEI_BATT_PER_KWH = 1350;
// Huawei's SMALL product (user 2026-09-26): 7-kWh modules on a power module, up to NINE
// modules = 63 kWh, at RM15,000 a module — the "Huawei Battery 7kWh - Power Module" add-on.
// That is RM2,143/kWh against RM1,350 for the 241-kWh commercial block, which is normal for
// the modular class. Above 63 kWh the 241-kWh blocks take over, exactly as GoodWe steps from
// 16-kWh LV units to 261-kWh HV blocks.
export const HUAWEI_MODULE_KWH = 7;
export const HUAWEI_MODULE_PRICE = 15000;
export const HUAWEI_MODULE_MAX = 9;                                    // 63 kWh
export const HUAWEI_MODULE_MAX_KWH = HUAWEI_MODULE_KWH * HUAWEI_MODULE_MAX;
const isHuaweiBatt = (brand) => String(brand || '').toLowerCase() === BATT_BRAND_HUAWEI;

export const batteryCostFor = (kwh, brand = BATT_BRAND_GOODWE) => {
    const b = Number(kwh) || 0;
    if (b <= 0) return 0;
    if (isHuaweiBatt(brand)) {
        // Priced per MODULE, not per kWh, while inside the modular range.
        if (b <= HUAWEI_MODULE_MAX_KWH) return Math.ceil(b / HUAWEI_MODULE_KWH) * HUAWEI_MODULE_PRICE;
        return b * HUAWEI_BATT_PER_KWH;
    }
    if (b < 100) return Math.ceil(b / 16) * LV_BATT_UNIT_NET;
    return b * (b <= 1000 ? 1200 : 1100);
};

// Human label for the breakdown line under the stepper, e.g. "482kWh Huawei HV × RM1,350/kWh".
export const batteryBrandLabel = (brand) => (isHuaweiBatt(brand) ? 'Huawei' : 'GoodWe');

// Battery step ladder (user 2026-07-10; 100-kWh unit removed 2026-07-27). 0 = none.
// LV = 16-kWh units (16..96 → Hybrid). HV = 261-kWh units only (261·522·783·1044… → String).
export const BATT_STEPS = (() => {
    const lv = [16, 32, 48, 64, 80, 96];
    const hv = [];
    for (let c = 1; c <= 11; c++) {          // 261-kWh building block only (no 100-kWh unit)
        const t = c * 261;
        if (t <= 3000) hv.push(t);
    }
    return [0, ...lv, ...hv];
})();
// Huawei ladder: 7-kWh modules up to nine (7…63), then the 241-kWh commercial blocks, same
// ~3,000 kWh ceiling as the GoodWe HV ladder. (user 2026-09-26)
export const HUAWEI_BATT_STEPS = (() => {
    const s = [0];
    for (let m = 1; m <= HUAWEI_MODULE_MAX; m++) s.push(m * HUAWEI_MODULE_KWH);
    for (let c = 1; c * HUAWEI_BATT_BLOCK_KWH <= 3000; c++) s.push(c * HUAWEI_BATT_BLOCK_KWH);
    return s;
})();
export const battStepsFor = (brand) => (isHuaweiBatt(brand) ? HUAWEI_BATT_STEPS : BATT_STEPS);

export const nextBattStep = (cur, brand = BATT_BRAND_GOODWE) => {
    const steps = battStepsFor(brand);
    const c = Number(cur) || 0;
    const hit = steps.find(s => s > c);
    return hit === undefined ? steps[steps.length - 1] : hit;
};
export const prevBattStep = (cur, brand = BATT_BRAND_GOODWE) => {
    const steps = battStepsFor(brand);
    const c = Number(cur) || 0;
    const below = steps.filter(s => s < c);
    return below.length ? below[below.length - 1] : 0;
};
// Snap a kWh value onto a brand's ladder when the brand is switched: the smallest step that
// covers the current size (0 stays 0). e.g. GoodWe 261 → Huawei 482; Huawei 482 → GoodWe 522.
export const snapBattToBrand = (cur, brand) => {
    const c = Number(cur) || 0;
    if (c <= 0) return 0;
    const steps = battStepsFor(brand);
    const hit = steps.find(s => s >= c);
    return hit === undefined ? steps[steps.length - 1] : hit;
};
// Battery ≥ 100 kWh = HV (AC-coupled) → String inverter; 1..96 kWh = LV → Hybrid.
export const isHvBattery = (kwh) => (Number(kwh) || 0) >= 100;

// Commercial O&M (maintenance) annual fee by kWp tier — Basic vs Premium package
// (official ATAP Solar maintenance table). Basic is Not Available above 100 kWp.
export const MAINT_TIERS = [
    { max: 6.82,  basic: 600,  premium: 1800 },
    { max: 10.54, basic: 800,  premium: 2300 },
    { max: 20,    basic: 800,  premium: 2600 },
    { max: 40,    basic: 1700, premium: 3500 },
    { max: 60,    basic: 2300, premium: 4500 },
    { max: 80,    basic: 2600, premium: 4500 },
    { max: 100,   basic: 2800, premium: 4500 },
    { max: 300,   basic: null, premium: 5000 },
    { max: 600,   basic: null, premium: 7500 },
    { max: 1000,  basic: null, premium: 11000 },
];
// pkg: 'basic' | 'premium'. Falls back to premium where basic is N/A (>100 kWp).
export const omForKwp = (kwp, pkg = 'premium') => {
    const k = Number(kwp) || 0;
    for (const t of MAINT_TIERS) if (k <= t.max) { const v = pkg === 'basic' ? t.basic : t.premium; return v != null ? v : t.premium; }
    return 11000;
};

// Largest license-free (≤72 kW) candidate ≈ the 96 kWp package — used to keep %
// cards out of the 72–100 kW dead zone. `candidates` = the sizing hook's candidate list.
export const max72Card = (candidates) => (candidates || []).filter(c => c.inverterKw <= 72).reduce((b, c) => (!b || c.kwp > b.kwp ? c : b), null);

// Dead-zone pull-back, shared by every sizing path. Above 72 kW the site needs an ST
// licence and a chargeman (a running cost), so a 73–89 kW proposal is the worst of both:
// the licence cost without the scale that pays for it. If `picked` is above 72 kW but the
// NATURAL need (target kWh ÷ generation per kWp ÷ 1.35, in 5 kW steps) is under 90 kW,
// propose the largest 72 kW package instead. ≥ 90 kW keeps the bigger system.
// (pickPct had this since 2026-06; the bill-target path did not, so an RM 8,549 bill
// proposed 80 kW / 115.7 kWp — user 2026-09-22: "72kW is best, avoid 80kW".)
export const pullBackTo72 = (picked, targetKwh, candidates) => {
    const list = candidates || [];
    const max72 = max72Card(list);
    if (!picked || !max72 || !(picked.inverterKw > 72)) return picked;
    const genPerKwp = list[0]?.kwp > 0 ? list[0].generated / list[0].kwp : 0;
    const reqKwp = genPerKwp > 0 ? (Number(targetKwh) || 0) / genPerKwp : 0;
    const natInv = Math.max(5, Math.ceil(reqKwp / 1.35 / 5) * 5);
    if (natInv < 90) return max72;
    // Genuinely big (≥ 90 kW need) but the pick still landed in the 73–89 kW band (e.g. an
    // 80 kW inverter was the smallest to reach the target): step up to the smallest ≥ 90 kW
    // inverter, filled to its max panels — never propose 73–89 kW. (user 2026-09-22)
    if (picked.inverterKw < 90) {
        const big = list.filter(c => c.inverterKw >= 90);
        if (big.length) {
            const inv = big.reduce((m, c) => Math.min(m, c.inverterKw), Infinity);
            return big.filter(c => c.inverterKw === inv).reduce((b, c) => (c.panels > b.panels ? c : b));
        }
    }
    return picked;
};

// Smallest NEM system whose monthly saving covers ≥ frac of the original TNB bill;
// falls back to the candidate closest to the target. Returns the SMALLEST system that
// hits the target — NO inverter-fill / ballooning.
// ctx: { candidates, bill, tariffRate }
export const pickPct = (frac, ctx) => {
    const { candidates, bill, tariffRate } = ctx;
    const list = candidates || [];
    if (!list.length) return null;
    const billN = parseFloat(bill) || 0;
    // Target the % of bill saved by GENERATION (gross), NOT net of the ST license
    // fee — a "~50%" card sizes to offset 50% of the bill, it does not over-size
    // to recover the RM800 license (per user: 14k → 133 kWp, not 149 kWp).
    // Bill-only sizing: % of bill measured by the REFERENCE saving (full generation
    // × tariff), so the kWp doesn't shrink/grow with operating days (see pickByGross).
    const gpct = (c) => billN > 0 ? ((Number(c.generated) || 0) * (tariffRate || 0)) / billN : (c.savingPct || 0);
    const pool = list.filter(c => gpct(c) >= frac && c.roiYears != null);
    const picked = pool.length
        ? pool.reduce((b, c) => (c.panels < b.panels ? c : b), pool[0])
        : list.reduce((b, c) => (Math.abs(gpct(c) - frac) < Math.abs(gpct(b) - frac) ? c : b), list[0]);
    // Dead-zone cap: a 73–89 kW natural need pulls back to the 72 kW package; ≥90 kW
    // keeps the bigger system (break-free at 90, was 100). Same helper as billTargetCard.
    return pullBackTo72(picked, frac * (billN / (tariffRate || 1)), candidates);
};

// MAX MD caps at the SAVING PLATEAU — the smallest system that already reaches
// peak monthly saving. proposals.maxSaving picks the smallest INVERTER whose
// generation covers consumption; fillInverter then fills panels to the MOST
// that inverter can carry at ratio <1.35 — per user, "max panels the kW can support".
export const fillInverter = (card, candidates) => {
    if (!card) return card;
    const same = (candidates || []).filter(c => c.inverterKw === card.inverterKw);
    return same.length ? same.reduce((b, c) => (c.panels > b.panels ? c : b), card) : card;
};

// Max panels an inverter can carry at the ratio cap (ratio = kWp / inverterKw ≤ cap).
// Used when the user changes the inverter kW directly: the panels auto-refill so the
// ratio stays valid (never over 1.42) — the inverter and panels are kept in lock-step.
// (user 2026-07-22)
export const fillPanelsForInverter = (inverterKw, wpk = 0.65, ratioCap = 1.45) => {
    const inv = Number(inverterKw) || 0;
    const w = Number(wpk) || 0.65;   // fallback = 650Wp default (callers pass the real wpk)
    return Math.max(1, Math.floor((inv * ratioCap) / w));
};

// "Propose 96 kWp straight if near" — when a card lands at ~88–96 kWp and the MD can
// carry a 72 kW inverter (snap96 exists), snap to the standard 96 kWp / 72 kW ST-free
// package.
//
// EVERY basis, SELCo included (user 2026-06-20). SELCo used to be excluded, and the
// result was Option A stalling at ~95 kWp: an MD of 72 floors the natural inverter to
// 70 kW, which caps the array at 94.5 kWp. 96 kWp on a 72 kW inverter is a 1.333 ratio
// — inside the 1.35 cap and still ST-free — so the snap is right there too.
//
// `basis` stays in the signature: every caller passes it, and it reads as the thing the
// decision is about even though the answer no longer depends on it.
export const snapNear96 = (card, basis, snap96) => {    // eslint-disable-line no-unused-vars
    if (!card || !snap96) return card;
    return (card.kwp >= 88 && card.kwp < 96) ? snap96 : card;
};

// ── Option A sizing: bill → TARGET monthly saving lookup (user 2026-06-22) ──
// The agent's manual method: read a target monthly saving off the bill, then size
// the SMALLEST system whose generation × tariff (grossMonthlySaving) reaches it.
// 640 Wp panels, capped at the inverter's 1.35 ratio (the candidate list already
// respects that cap). Bills under RM 1.4k cover ~the full bill ("Max"). The 5,000
// target lands at ~96 kWp @ 0.515 — matches the hand-quote.
export const targetSavingForBill = (billN) => {
    if (billN <= 0) return 0;
    // Single curve (user 2026-06-24):
    //   • ~87% of the bill for small/mid bills → e.g. RM2k ≈ 52 panels (~33 kWp).
    //   • FLOORED at 5,000 = the 96.64 kWp license-free max → RM7k/7.5k land at 96.64.
    //   • capped at 65% of the bill → once 65% > 5k (~RM7.7k+) it climbs past 96 kWp,
    //     and the ≥90 kW break-free grows the big bills (RM20k/30k → ~128 kWp+).
    // pickByGross then takes the smallest 5 kW inverter that reaches the target and
    // fillInverter fills panels to that inverter's max kWp.
    return Math.min(Math.round(billN * 0.87), Math.max(5000, Math.round(billN * 0.65)));
};

// ctx: { candidates, tariffRate }
export const pickByGross = (targetRm, ctx) => {
    const { candidates, tariffRate } = ctx;
    const list = (candidates || []).filter(c => c.roiYears != null);
    if (!list.length) return null;
    // Size from the BILL ONLY: match the target against the REFERENCE saving =
    // full generation × tariff (i.e. 100% daytime, every day self-used), which is
    // operating-days-INDEPENDENT — so the kWp is fixed by the bill. The displayed
    // saving then floats with the customer's actual operating days (fewer days →
    // less saving), instead of the engine growing the kWp to pin the saving.
    // (user 2026-06-24: "bill decides the kWp; operating days lower → saving lesser")
    const refSave = (c) => (Number(c.generated) || 0) * (tariffRate || 0);
    const reach = list.filter(c => refSave(c) >= targetRm);
    return reach.length
        ? reach.reduce((b, c) => (c.panels < b.panels ? c : b), reach[0])   // smallest that REACHES the target
        : list.reduce((b, c) => (refSave(c) > refSave(b) ? c : b), list[0]); // none reaches → biggest generation
};

// ctx: { candidates, bill, tariffRate }
export const billTargetCard = (ctx) => {
    const { candidates, bill, tariffRate, mode = 'atap', daytimeFrac = 1, hasBattery = false } = ctx;
    // SELCo (zero export) with NO battery: any daytime generation above the instantaneous
    // load is CURTAILED (wasted), so a bigger kWp buys almost no extra saving. Size to the
    // DAYTIME self-use instead — scale the sizing bill by the daytime fraction (floored at
    // 0.4 so a very low daytime% can't collapse to a 1-panel system). ATAP exports the
    // surplus at SMP, and a battery STORES it for off-hours — both cases keep FULL sizing
    // (factor = 1). e.g. RM 2,000 bill @ 77% daytime → sizes like RM 1,540 → ~28 kWp instead
    // of the 42 kWp LV band. (user 2026-08-02)
    const _dtF = (mode === 'selco' && !hasBattery)
        ? Math.max(0.4, Math.min(1, Number(daytimeFrac) || 1))
        : 1;
    const billN = (parseFloat(bill) || 0) * _dtF;
    // Low-Voltage step bands (LV RM0.515 only): hold each inverter across a bill
    // RANGE so the recommended system doesn't over-size — a too-large system pushed
    // the offset to ~99% of the bill (unrealistic) and blanked the After-Solar box.
    // fillInverter then fills to that inverter's max panels @ 1.42 ratio.
    //   < 2.4k       → 30 kW → 42.24 kWp   (was 25 kW; not a real model)
    //   2.4k – 2.79k → 30 kW → 42.24 kWp
    //   2.8k – 3.29k → 36 kW → 50.56 kWp   (was 35 kW → real string model 36)
    //   3.3k – 3.89k → 40 kW → 56.32 kWp
    //   3.9k – 4.49k → 50 kW → 70.40 kWp   (skips 45 kW)
    //   4.5k – 5.49k → 60 kW → 85.12 kWp   (was 55 kW; not a real model)
    //   5.5k – 5.99k → 60 kW → 85.12 kWp
    // (user 2026-07-06 — realistic commercial coverage; bands repointed to real
    //  inverter models on 2026-07-22 when inverter sizing moved to model-step.)
    // ≥ 6k resumes the target curve → 101.76 kWp (72 kW).
    // Only applies when a candidate with that inverter exists (SELCo / no-MD case).
    const isLV = Math.abs((Number(tariffRate) || 0) - 0.515) < 1e-6;
    if (isLV && billN >= 2000 && billN < 6000) {
        // Real inverter models only (2026-07-22): the ladder is 20/30/36/40/50/60/72…,
        // so 25/35/55 kW no longer exist and each band snaps to the nearest real model.
        const wantInv = billN < 2400 ? 30
            : billN < 2800 ? 30
            : billN < 3300 ? 36   // was 35 kW → 36 (real)
            : billN < 3900 ? 40
            : billN < 4500 ? 50   // skip 45 kW
            : billN < 5500 ? 60   // was 55 kW → 60 (real)
            : 60;                 // 5500–5999
        const c = (candidates || []).find(cc => cc.inverterKw === wantInv);
        if (c) return fillInverter(c, candidates);
    }
    const t = targetSavingForBill(billN);
    // The target picks the INVERTER; then FILL that inverter to its max panels at the
    // 1.35 ratio — don't leave inverter headroom unused (user: "use the inverter max
    // panels"). e.g. 5,000 target → 72 kW inverter → 151 panels = 96.64 kWp (= floor
    // of 72×1.35÷0.64; 152 panels would exceed 97.2 kWp). (2026-06-23)
    const card = t === 'max' ? pickPct(1.00, ctx) : pickByGross(t, ctx);
    const filled = fillInverter(card, candidates);
    // Stay at 72 kW (no ST licence / chargeman) unless ≥ 90 kW is genuinely needed.
    // pickPct('max') already applies this; the target path now does too. (user 2026-09-22)
    return t === 'max' ? filled : pullBackTo72(filled, t / (tariffRate || 1), candidates);
};

/* ── Operation warranty: the years, and the free one-time cleaning ──────────────────
 * The dropdown carries BOTH in one string — "3 Years (One-time Cleaning)" — because the
 * cleaning is not a separate choice to make: it is included from CLEANING_MIN_KWP up and
 * is not offered at all below it, so an agent cannot promise it on a small system by
 * accident. (user 2026-10-14)
 *
 * The two still have to be READ apart, and they were not. The proposal's free-O&M line
 * and the WhatsApp warranty line each parsed the YEARS out of that string and then
 * printed "One-time Cleaning" unconditionally — so a system under the threshold, whose
 * dropdown plainly says "1 Year" with no cleaning, was handed a proposal and a WhatsApp
 * quote promising a free clean it does not include.
 * (user 2026-10-26: "operation warranty is separated with cleaning? but the quotation
 *  show cleaning if got 1 time cleaning")
 *
 * Shared from here because cscCombinedTab and cscMultiMeterTab each had their own copy
 * of the first two, and a rule written twice drifts.
 */
export const CLEANING_MIN_KWP = 30;

/** The two inputs the agent now sees. Cleaning is only offered from 30 kWp up. */
export const WARRANTY_TERM_OPTIONS = ['1 Year', '3 Years'];
export const cleaningOptionsFor = (kwp) => ((Number(kwp) || 0) > CLEANING_MIN_KWP
    ? ['No Cleaning', '1 Time Cleaning', '2 Times Cleaning', '3 Times Cleaning']
    : ['No Cleaning']);

/**
 * The ONE value that is stored and quoted, composed from the two inputs:
 *   ("3 Years", 1) -> "3 Years (1 Time Cleaning)"
 *   ("3 Years", 0) -> "3 Years"
 *
 * Kept as a single string on purpose. It is what the sale record, the quotation PDF and
 * every saved draft already carry, so splitting the INPUT costs no migration and no
 * backend DTO change — a quote written last month still reads back correctly.
 * (user 2026-10-26: "The quotation dont split just the input split" / "If got include
 *  cleaning only write like existing one 3years and 1time cleaning")
 */
export const composeWarranty = (term, times) => {
    const t = String(term || '1 Year').trim();
    const n = Math.max(0, Math.round(Number(times) || 0));
    return n > 0 ? `${t} (${n} Time${n > 1 ? 's' : ''} Cleaning)` : t;
};

/** How many cleans this warranty carries. Understands the older "(One-time Cleaning)". */
export const warrantyCleaningTimes = (value) => {
    const s = String(value || '');
    const m = s.match(/\((\d+)\s*Times?\s*Cleaning\)/i);
    if (m) return Number(m[1]) || 0;
    return /one-?time\s*cleaning/i.test(s) ? 1 : 0;
};

export const warrantyOptionsFor = (kwp) => ((Number(kwp) || 0) > CLEANING_MIN_KWP
    ? ['1 Year (1 Time Cleaning)', '3 Years (1 Time Cleaning)']
    : ['1 Year', '3 Years']);

/**
 * What the cleaning should be for a system of this size, left alone.
 * Above 30 kWp one free clean is included; at or below it there is none.
 * (user 2026-10-26: "Default no cleaning for 30kwp n below, more than 30kwp got free
 *  1 time cleaning")
 */
export const defaultCleaningTimes = (kwp) => ((Number(kwp) || 0) > CLEANING_MIN_KWP ? 1 : 0);

/** The O&M term a new quote starts on. (user 2026-10-26: "Onm by default 1 year") */
export const DEFAULT_WARRANTY_TERM = '1 Year';

/**
 * Reconcile the stored warranty with the system size.
 *
 * The TERM is always preserved — it is a commercial choice, not a function of kWp.
 *
 * The CLEANING follows the size until the agent touches it. Untouched, it is simply the
 * default for that size, so growing a system past 30 kWp picks the free clean up and
 * shrinking it drops one again. Once `touched`, the agent's choice stands and is only
 * CLAMPED: a clean chosen at 120 kWp still cannot survive a redesign to 20, because the
 * promise must not outlive the system that justified it.
 */
export const warrantyForKwp = (value, kwp, touched = false) => {
    const years = parseInt(String(value || ''), 10) || 1;
    const term = `${years} Year${years > 1 ? 's' : ''}`;
    const allowed = (Number(kwp) || 0) > CLEANING_MIN_KWP;
    if (!touched) return composeWarranty(term, defaultCleaningTimes(kwp));
    return composeWarranty(term, allowed ? warrantyCleaningTimes(value) : 0);
};

/** The years alone, for the lines that only want a number. */
export const warrantyYears = (value) => parseInt(String(value || ''), 10) || 1;

/** The term alone, for a Qty column: "3 Years (One-time Cleaning)" -> "3 Years".
 *  Mirrors WarrantyYearsOnly in QuotationPdfService.cs — the preview and the PDF are
 *  the same document, so the two must strip it the same way. */
export const warrantyTermOnly = (value) => {
    const s = String(value || '').trim();
    if (!s) return '1 Year';
    const bracket = s.indexOf('(');
    const term = (bracket > 0 ? s.slice(0, bracket) : s).trim();
    return term || '1 Year';
};

/**
 * Does THIS warranty actually include the free clean?
 *
 * Read from the VALUE, never re-derived from kWp: a saved quote or a restored draft must
 * keep whatever it was sold with, even if the system size has been edited since.
 */
export const warrantyHasCleaning = (value) => /cleaning/i.test(String(value || ''));
